import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, useMantineTheme } from "@mantine/core";
import { KineticPerimetryStimulus } from "types/testTypes";

/**
 * Props for the KineticPerimetryStimulusViewer component
 */
interface KineticPerimetryStimulusViewerProps {
  /** Maximum angle for the radial grid (e.g., 70 degrees) */
  maxAngle: number;
  /** Angle for dividing the circle into equal parts (e.g., 60 degrees would create 6 lines) */
  divisionAngle: number;
  /** Responses from the test */
  responses?: KineticPerimetryStimulus[];
  /** Stimulus presented during the test */
  stimulus?: KineticPerimetryStimulus;
  /** Size of the component (width and height will be equal) */
  size?: number | string;
  /** Optional custom styles */
  style?: React.CSSProperties;
  /** Step size for the concentric circles (in degrees) */
  angleStep?: number;
}

/**
 * Component that visualizes kinetic perimetry test stimulus and responses
 */
const KineticPerimetryStimulusViewer: React.FC<KineticPerimetryStimulusViewerProps> = ({
  maxAngle = 70,
  divisionAngle = 60,
  responses = [],
  stimulus,
  style,
  angleStep = 10,
}) => {
  const theme = useMantineTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const updateDimensions = () => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      // Keep aspect ratio 1:1 for a perfect circle
      const size = Math.min(clientWidth, clientHeight);
      setDimensions({
        width: size,
        height: size,
      });
    }
  };

  // Initial dimension setup
  useEffect(() => {
    updateDimensions();
  }, []);

  // Handle resize events with both window resize and ResizeObserver
  useEffect(() => {
    const handleResize = () => {
      updateDimensions();
    };

    // Set up window resize listener
    window.addEventListener("resize", handleResize);

    // Set up ResizeObserver for more precise control
    if (containerRef.current && typeof ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });

      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", handleResize);
      };
    }

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const centerX = dimensions.width / 2;
  const centerY = dimensions.height / 2;

  const radius = Math.min(centerX, centerY) * 0.9;

  const getAbsoluteStimulusIndex = useCallback(
    (fixationPointIndex: number, stimulusIndex: number): number => {
      const totalStimuli = Math.ceil(360 / divisionAngle);
      const stimuliPerGroup = Math.floor(totalStimuli / 4);
      const remainder = totalStimuli % 4;

      let stimuliBeforeThisGroup = 0;
      for (let i = 0; i < fixationPointIndex; i++) {
        const stimuliInGroup = i < remainder ? stimuliPerGroup + 1 : stimuliPerGroup;
        stimuliBeforeThisGroup += stimuliInGroup;
      }

      return stimuliBeforeThisGroup + stimulusIndex;
    },
    [divisionAngle],
  );

  const concentricCircles = useMemo(() => {
    const circles = [];

    for (let angle = angleStep; angle <= maxAngle; angle += angleStep) {
      // Calculate radius ratio based on angle
      const radiusRatio = angle / maxAngle;
      circles.push({
        radius: radius * radiusRatio,
        angle: angle,
      });
    }

    return circles;
  }, [maxAngle, radius, angleStep]);

  // Generate division lines at all possible stimulus angles
  const divisionLines = useMemo(() => {
    const lines = [];
    const totalStimuli = Math.ceil(360 / divisionAngle);

    for (let i = 0; i < totalStimuli; i++) {
      const angle = i * divisionAngle;
      const radian = (angle * Math.PI) / 180;

      const endX = centerX + Math.cos(radian) * radius;
      const endY = centerY - Math.sin(radian) * radius;

      lines.push({
        angle: angle,
        start: { x: centerX, y: centerY },
        end: { x: endX, y: endY },
      });
    }

    return lines;
  }, [divisionAngle, centerX, centerY, radius]);

  const responsePoints = useMemo(() => {
    if (!responses || responses.length === 0) return [];

    return responses.map((response) => {
      const absoluteStimulusIndex = getAbsoluteStimulusIndex(
        response.fixationPointIndex,
        response.stimulusIndex,
      );

      const lineAngle = absoluteStimulusIndex * divisionAngle;
      const radian = (lineAngle * Math.PI) / 180;

      const distance = radius - radius * response.distanceRatio;

      return {
        x: centerX + Math.cos(radian) * distance,
        y: centerY - Math.sin(radian) * distance,
        angle: lineAngle,
        response: response,
      };
    });
  }, [responses, divisionAngle, centerX, centerY, radius, getAbsoluteStimulusIndex]);

  const sortedResponsePoints = useMemo(() => {
    return [...responsePoints].sort((a, b) => a.angle - b.angle);
  }, [responsePoints]);

  const polygonPath = useMemo(() => {
    if (sortedResponsePoints.length < 3) return "";

    const pathData = sortedResponsePoints
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");

    return pathData + " Z"; // Close the path
  }, [sortedResponsePoints]);

  const lastStimulus = useMemo(() => {
    if (!stimulus) return null;

    const absoluteStimulusIndex = getAbsoluteStimulusIndex(
      stimulus.fixationPointIndex,
      stimulus.stimulusIndex,
    );

    const lineAngle = absoluteStimulusIndex * divisionAngle;
    const radian = (lineAngle * Math.PI) / 180;

    const distance = radius - radius * stimulus.distanceRatio;

    return {
      x: centerX + Math.cos(radian) * distance,
      y: centerY - Math.sin(radian) * distance,
      stimulus: stimulus,
    };
  }, [stimulus, divisionAngle, centerX, centerY, radius, getAbsoluteStimulusIndex]);

  return (
    <Box
      ref={containerRef}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${dimensions.width || 100} ${dimensions.height || 100}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: "visible", margin: "auto", display: "block" }}
      >
        {concentricCircles.map((circle, index) => (
          <React.Fragment key={`circle-${index}`}>
            <circle
              cx={centerX}
              cy={centerY}
              r={circle.radius}
              fill="none"
              stroke={theme.colors.gray[6]}
              strokeWidth={0.5}
              strokeDasharray="2,2"
            />
            <text
              x={centerX}
              y={centerY - circle.radius - 5}
              textAnchor="middle"
              dominantBaseline="auto"
              fill={theme.colors.gray[6]}
              fontSize={10}
            >
              {circle.angle}°
            </text>
          </React.Fragment>
        ))}

        {divisionLines.map((line, index) => (
          <line
            key={`line-${index}`}
            x1={line.start.x}
            y1={line.start.y}
            x2={line.end.x}
            y2={line.end.y}
            stroke={theme.colors.blue[5]}
            strokeWidth={1}
          />
        ))}

        {polygonPath && (
          <path
            d={polygonPath}
            fill={theme.colors.red[1]}
            fillOpacity={0.3}
            stroke={theme.colors.red[6]}
            strokeWidth={2}
          />
        )}

        {responsePoints.map((point, index) => (
          <circle
            key={`response-${index}`}
            cx={point.x}
            cy={point.y}
            r={3}
            fill={theme.colors.red[6]}
          ></circle>
        ))}
        {stimulus && (
          <circle
            key={`stimulus-${stimulus?.stimulusIndex}`}
            cx={lastStimulus?.x}
            cy={lastStimulus?.y}
            r={3}
            fill={theme.colors.blue[6]}
          ></circle>
        )}

        <circle cx={centerX} cy={centerY} r={3} fill={theme.colors.dark[7]} strokeWidth={1} />
      </svg>
    </Box>
  );
};

export default KineticPerimetryStimulusViewer;
